import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { ArgoCdApp } from '../api'
import { PageDescription } from '../components/PageDescription'

// ---- Status helpers ----

const SYNC_STYLES = {
    Synced:    { badge: 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30', dot: 'bg-emerald-400', label: 'Synced' },
    OutOfSync: { badge: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30',       dot: 'bg-amber-400',   label: 'Out of sync' },
    Unknown:   { badge: 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30',       dot: 'bg-slate-500',   label: 'Unknown' },
} satisfies Record<string, { badge: string; dot: string; label: string }>

const HEALTH_STYLES = {
    Healthy:     { badge: 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30', dot: 'bg-emerald-400' },
    Degraded:    { badge: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30',             dot: 'bg-red-500' },
    Progressing: { badge: 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30',          dot: 'bg-blue-400' },
    Missing:     { badge: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30',             dot: 'bg-red-500' },
    Unknown:     { badge: 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30',       dot: 'bg-slate-500' },
} satisfies Record<string, { badge: string; dot: string }>

type SyncKey   = keyof typeof SYNC_STYLES
type HealthKey = keyof typeof HEALTH_STYLES

function syncStyle(status: string)   { return SYNC_STYLES[status   as SyncKey]   ?? SYNC_STYLES.Unknown }
function healthStyle(status: string) { return HEALTH_STYLES[status as HealthKey] ?? HEALTH_STYLES.Unknown }

function shortRevision(rev: string | null) {
    if (!rev) return null
    // Trim leading 'sha-' prefix if ArgoCD adds it, then take first 12 chars
    const clean = rev.startsWith('sha-') ? rev.slice(4) : rev
    return clean.length > 12 ? clean.slice(0, 12) : clean
}

function relativeTime(iso: string | null): string {
    if (!iso) return '—'
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60_000)
    if (mins < 1)   return 'just now'
    if (mins < 60)  return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24)   return `${hrs}h ago`
    return `${Math.floor(hrs / 24)}d ago`
}

// ---- App row ----

interface AppRowProps {
    app: ArgoCdApp
    visible: boolean
    delay: number
}

function AppRow({ app, visible, delay }: AppRowProps) {
    const queryClient = useQueryClient()
    const [showError, setShowError] = useState(false)

    const refreshMutation = useMutation({
        mutationFn: () => releaseApi.hardRefreshArgoCdApp(app.name),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['argocd-apps'] }),
    })

    const syncMutation = useMutation({
        mutationFn: () => releaseApi.forceSyncArgoCdApp(app.name),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['argocd-apps'] }),
    })

    const sync   = syncStyle(app.syncStatus)
    const health = healthStyle(app.healthStatus)
    const rev    = shortRevision(app.revision)
    const hasError = !!app.lastSyncError

    return (
        <div
            className="transition-all duration-300"
            style={{
                transitionDelay: `${delay}ms`,
                opacity: visible ? 1 : 0,
            }}
        >
            <div className="group flex items-center gap-4 px-5 py-3">
                {/* App name */}
                <span className="text-sm font-mono text-slate-200 w-48 shrink-0 truncate">{app.name}</span>

                {/* Sync badge */}
                <span className="w-24 shrink-0">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${sync.badge}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${sync.dot}`} />
                        {sync.label}
                    </span>
                </span>

                {/* Health badge */}
                <span className="w-24 shrink-0">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${health.badge}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${health.dot}`} />
                        {app.healthStatus}
                    </span>
                </span>

                {/* Revision */}
                <span className="text-xs font-mono text-slate-500 w-28 shrink-0 truncate">
                    {rev ? rev : <span className="text-slate-700">—</span>}
                </span>

                {/* Last sync */}
                <span className="text-xs text-slate-500 shrink-0">
                    {relativeTime(app.lastSyncTime)}
                </span>

                {/* Error toggle */}
                {hasError && (
                    <button
                        onClick={() => setShowError(v => !v)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors shrink-0"
                    >
                        {showError ? '▲ error' : '▼ error'}
                    </button>
                )}

                {/* Repo path — muted */}
                {app.repoPath && !hasError && (
                    <span className="text-[10px] text-slate-600 ml-auto font-mono truncate max-w-[200px]">
                        {app.repoPath}
                    </span>
                )}

                {/* Action buttons — visible on row hover */}
                <div className="ml-auto flex items-center gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={() => refreshMutation.mutate()}
                        disabled={refreshMutation.isPending || syncMutation.isPending}
                        title="Hard refresh — re-fetch manifests from git and discard render cache"
                        className="text-[10px] px-2 py-0.5 rounded border border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200 disabled:opacity-40 transition-colors"
                    >
                        {refreshMutation.isPending ? '…' : '⟳ Refresh'}
                    </button>
                    <button
                        onClick={() => syncMutation.mutate()}
                        disabled={syncMutation.isPending || refreshMutation.isPending}
                        title="Force sync — apply manifests to cluster now"
                        className="text-[10px] px-2 py-0.5 rounded border border-blue-700/50 text-blue-400 hover:border-blue-500 hover:text-blue-200 disabled:opacity-40 transition-colors"
                    >
                        {syncMutation.isPending ? '…' : '↑ Sync'}
                    </button>
                </div>

                {/* Mutation errors */}
                {(refreshMutation.isError || syncMutation.isError) && (
                    <span
                        className="text-[10px] text-red-400 shrink-0 max-w-[200px] truncate"
                        title={(syncMutation.error ?? refreshMutation.error) instanceof Error
                            ? (syncMutation.error ?? refreshMutation.error)!.message
                            : 'Failed'}
                    >
                        {(syncMutation.error ?? refreshMutation.error) instanceof Error
                            ? (syncMutation.error ?? refreshMutation.error)!.message
                            : 'Failed'}
                    </span>
                )}
            </div>

            {/* Sync error detail */}
            {showError && hasError && (
                <div className="mx-5 mb-3 px-3 py-2 rounded-lg bg-red-950/50 border border-red-800/50 text-xs font-mono text-red-300 whitespace-pre-wrap break-all">
                    {app.lastSyncError}
                </div>
            )}
        </div>
    )
}

// ---- Namespace group ----

interface NamespaceGroupProps {
    namespace: string
    apps: ArgoCdApp[]
    visible: boolean
    delay: number
}

function NamespaceGroup({ namespace, apps, visible, delay }: NamespaceGroupProps) {
    const hasError   = apps.some(a => a.syncStatus === 'OutOfSync' || a.healthStatus === 'Degraded' || a.healthStatus === 'Missing')
    const allHealthy = apps.every(a => a.syncStatus === 'Synced' && a.healthStatus === 'Healthy')
    const groupStyle = allHealthy
        ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30'
        : hasError
            ? 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30'
            : 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30'

    return (
        <div
            className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden transition-all duration-500"
            style={{
                transitionDelay: `${delay}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(16px)',
            }}
        >
            {/* Group header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/50 bg-slate-800/60">
                <span className="text-sm font-semibold text-white">{namespace}</span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${groupStyle}`}>
                    {apps.length} app{apps.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* App rows */}
            <div className="divide-y divide-slate-700/30">
                {apps.map((app, i) => (
                    <AppRow
                        key={app.name}
                        app={app}
                        visible={visible}
                        delay={delay + 60 + i * 40}
                    />
                ))}
            </div>
        </div>
    )
}

// ---- Main page ----

export function GitOpsPage() {
    const [visible, setVisible] = useState(false)

    const { data, isLoading, isError, dataUpdatedAt, refetch, isFetching } = useQuery({
        queryKey: ['argocd-apps'],
        queryFn: releaseApi.getArgoCdApps,
        refetchInterval: 30_000,
    })

    useEffect(() => {
        if (data) {
            const t = setTimeout(() => setVisible(true), 80)
            return () => clearTimeout(t)
        }
        return undefined
    }, [data])

    // Group apps by destination namespace, in ring order
    const ringOrder = ['dev', 'qa', 'ring0', 'ring1', 'ring2']
    const grouped = data
        ? Object.entries(
            data.apps.reduce<Record<string, ArgoCdApp[]>>((acc, app) => {
                const key = app.destinationNamespace || 'unset'
                ;(acc[key] ??= []).push(app)
                return acc
            }, {})
          ).sort(([a], [b]) => {
              const ai = ringOrder.indexOf(a)
              const bi = ringOrder.indexOf(b)
              if (ai === -1 && bi === -1) return a.localeCompare(b)
              if (ai === -1) return 1
              if (bi === -1) return -1
              return ai - bi
          })
        : []

    return (
        <div className="flex flex-col min-h-0 flex-1">
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">GitOps</span>
                        <PageDescription subtitle="ArgoCD application sync status" storageKey="desc-gitops" detail="Shows the sync and health status of every ArgoCD application in the cluster. A Synced + Healthy app means the live cluster matches the gitops repo. OutOfSync means a change is pending — click the app to see details and trigger a manual sync if needed." />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        {dataUpdatedAt > 0 && (
                            <span>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
                        )}
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 overflow-y-auto px-4 sm:px-6 py-8">
                {isLoading && (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-slate-500 text-sm animate-pulse">Fetching ArgoCD app status…</div>
                    </div>
                )}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm max-w-lg">
                        Failed to load ArgoCD status. Is the ops-api running with cluster RBAC and the argoproj.io CRD rule?
                    </div>
                )}

                {data && (
                    <div className="max-w-5xl mx-auto space-y-4">
                        {/* Column headers */}
                        <div
                            className="flex items-center gap-4 px-5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 transition-all duration-400"
                            style={{ opacity: visible ? 1 : 0, transitionDelay: '60ms' }}
                        >
                            <span className="w-48 shrink-0">Application</span>
                            <span className="w-24 shrink-0">Sync</span>
                            <span className="w-24 shrink-0">Health</span>
                            <span className="w-28 shrink-0">Revision</span>
                            <span className="shrink-0">Last sync</span>
                        </div>

                        {grouped.map(([ns, apps], i) => (
                            <NamespaceGroup
                                key={ns}
                                namespace={ns}
                                apps={apps}
                                visible={visible}
                                delay={100 + i * 80}
                            />
                        ))}

                        {grouped.length === 0 && (
                            <div className="text-sm text-slate-500 text-center py-16">
                                No ArgoCD applications found.
                            </div>
                        )}

                        {/* Legend */}
                        <div
                            className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 text-xs text-slate-500 transition-all duration-500"
                            style={{ opacity: visible ? 1 : 0, transitionDelay: `${100 + grouped.length * 80 + 80}ms` }}
                        >
                            <span className="font-medium text-slate-400">Sync:</span>
                            {[
                                { dot: 'bg-emerald-400', label: 'Synced' },
                                { dot: 'bg-amber-400',   label: 'Out of sync' },
                                { dot: 'bg-slate-500',   label: 'Unknown' },
                            ].map(({ dot, label }) => (
                                <span key={label} className="flex items-center gap-1.5">
                                    <span className={`h-2 w-2 rounded-full ${dot}`} />
                                    {label}
                                </span>
                            ))}
                            <span className="font-medium text-slate-400 ml-4">Health:</span>
                            {[
                                { dot: 'bg-emerald-400', label: 'Healthy' },
                                { dot: 'bg-blue-400',    label: 'Progressing' },
                                { dot: 'bg-red-500',     label: 'Degraded / Missing' },
                                { dot: 'bg-slate-500',   label: 'Unknown' },
                            ].map(({ dot, label }) => (
                                <span key={label} className="flex items-center gap-1.5">
                                    <span className={`h-2 w-2 rounded-full ${dot}`} />
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </main>
        </div>
    )
}
